/**
 * The report spec: a serializable description of one report.
 *
 * A spec is the unit that travels everywhere — it is the request body, it is a
 * saved report's stored settings, and it is a dashboard widget's settings. That
 * is why it is plain data with a Zod schema and no behaviour.
 *
 * Two shapes by composition: {@link UIReportSpec} is what the builder edits and
 * may have empty shelves; {@link ReportSpec} is complete enough to run, and is
 * the only one that is ever sent or saved.
 *
 * A spec also says WHICH KIND of report it is — a pivot that groups and
 * aggregates, or a detail listing of rows. See {@link ReportKind}, and
 * docs/reporting/report-kinds.md for why one compiler serves both.
 *
 * See docs/reporting/requirements.md §FR-2.
 */
import {
  DATE_RANGE_PRESETS,
  type DateRangePreset,
  type DateRangeValue,
  resolveDateRange,
} from '@clockwise/shared/date-range';
import { z } from 'zod';
import { filterNodeSchema } from './filter';

/**
 * Pivot or listing.
 *
 * A discriminator on ONE object with a default, not a discriminated union.
 * Every existing consumer — `planQuery`, `pivot`, the builder, a saved spec
 * written before this field existed — keeps working unchanged and reads as a
 * pivot. A union is better typed and can come later, once there are enough
 * detail reports to pay for touching every consumer.
 *
 * Each kind ignores the other's fields: detail ignores `rows`, `cols`,
 * `showAs`, `totals` and `subtotals`; pivot ignores `columns` and `offset`.
 * {@link validateSpec} enforces each side's rules — including the ones that
 * only make sense for one of them, like the group-by ceiling.
 */
export const REPORT_KINDS = ['pivot', 'detail'] as const;
export type ReportKind = (typeof REPORT_KINDS)[number];

/**
 * The two words the business chose, in ONE place.
 *
 * The builder prints them on its kind control, and a guard that refuses a kind
 * has to name it in the same words — "Utilization cannot be built as a Tabular
 * report" is only a usable sentence if "Tabular" is the word on the button the
 * reader just pressed. A second copy in the UI would be free to drift from the
 * one in the refusal, and the refusal is the half nobody re-reads.
 */
export const REPORT_KIND_LABELS: Record<ReportKind, string> = {
  pivot: 'Pivot',
  detail: 'Tabular',
};

/** How a measure's value is transformed for display. */
export const SHOW_AS_MODES = ['raw', 'pctRow', 'pctCol', 'pctTotal'] as const;
export type ShowAsMode = (typeof SHOW_AS_MODES)[number];

/**
 * The widest precision either renderer can actually honour.
 *
 * `Intl.NumberFormat` throws `RangeError` outside 0–100, and Excel refuses a
 * custom format carrying more than 30 decimals — it opens the workbook with a
 * repair prompt. So an out-of-range precision used to fail DIFFERENTLY on each
 * surface: a white-screened report from a thrown error on one, a silently
 * malformed download on the other.
 *
 * Here rather than in `model.ts` because BOTH the wire bound below and
 * `decimalsFor`'s guard have to be the same number, and `model.ts` already
 * imports this file — the other direction would be a cycle.
 */
export const MAX_DECIMALS = 20;

/** How many measures one spec may carry a format for. See `valueFormat`. */
export const MAX_VALUE_FORMATS = 500;

/**
 * How a negative reads.
 *
 * Five, not four. Excel and Sheets both offer a red negative with the MINUS
 * DROPPED (`redOnly`); `minusRed` keeps it. Both ship, because they answer
 * different questions — `redOnly` is what a finance reader recognises from the
 * spreadsheet, and `minusRed` is the one that survives greyscale printing and
 * a reader who cannot distinguish the colour. Which to use is the report
 * author's call, not this layer's.
 */
export const NEGATIVE_STYLES = ['minus', 'minusRed', 'redOnly', 'parens', 'parensRed'] as const;
export type NegativeStyle = (typeof NEGATIVE_STYLES)[number];

/** Does this style carry the sign in COLOUR? `redOnly` carries it in nothing else. */
export function isRedStyle(style: NegativeStyle): boolean {
  return style === 'redOnly' || style === 'minusRed' || style === 'parensRed';
}

/**
 * What an exact zero prints as.
 *
 * `dash` is a HYPHEN, not an em dash, and the difference is load-bearing: the
 * em dash is reserved for an ABSENT value ("no rows matched"), and a zero is
 * "matched, summing to nothing". Two different facts must not collide on one
 * glyph. The hyphen is also what Excel's own accounting format already uses.
 */
export const ZERO_STYLES = ['number', 'dash', 'blank'] as const;
export type ZeroStyle = (typeof ZERO_STYLES)[number];

/** How a currency measure wears its unit. */
export const SYMBOL_STYLES = ['symbol', 'code', 'none'] as const;
export type SymbolStyle = (typeof SYMBOL_STYLES)[number];

/**
 * What a value is DIVIDED BY for display, and the suffix that says so.
 *
 * Display only, as it is in a spreadsheet: `#,##0.0,,"M"` shows 1,234,567 as
 * `1.2M` and the cell still holds 1,234,567, so a total, a formula or a pasted
 * value is exact. `none` is not in the list because absence already means it —
 * see `ValueFormat`.
 */
export const SCALE_STYLES = ['thousands', 'millions'] as const;
export type ScaleStyle = (typeof SCALE_STYLES)[number];

/**
 * How many highlight rules one measure may carry.
 *
 * Three, and the bound is EDITORIAL rather than technical. A cell takes one
 * tone, so a fourth rule can only fire where three already did not — and a
 * column carrying four thresholds has stopped being a highlight and become a
 * chart nobody can read at a glance. Excel's own colour-scale presets stop at
 * three stops for the same reason.
 */
export const MAX_FORMAT_RULES = 3;

/**
 * When a highlight rule fires.
 *
 * Four comparisons, no `eq` and no `between`. `eq` on a measure is a coin
 * toss — these are sums of numerics, and asking a total of hours to equal 40.00
 * exactly is a rule that silently never fires. `between` is two rules, and
 * spelling it as one would need a second bound on every rule for the benefit of
 * the minority that want it; ordered rules already express a band, because the
 * FIRST match wins.
 */
export const RULE_OPS = ['lt', 'lte', 'gt', 'gte'] as const;
export type RuleOp = (typeof RULE_OPS)[number];

/**
 * The tones a rule may paint.
 *
 * NAMED, NEVER FREE HEX, and this is the load-bearing decision in the feature.
 * A tone resolves to a fill AND a readable text colour, per surface: CSS custom
 * properties that already follow the light/dark theme on screen, fixed ARGB in
 * the workbook. Free hex would have to answer none of that and would let an
 * author pick a fill their own text disappears against — a report that looks
 * fine on the machine it was built on and is unreadable on the next one.
 *
 * `grey` is here for "decided, not alarming" — an exempt row, a known-zero —
 * and it is the one tone that carries no verdict.
 *
 * EIGHT, AND THE FILL STRENGTH IS WHAT BUYS THE LAST TWO. Every tone paints a
 * tint of the surface, which drains most of the chroma out of the hue, so two
 * hues that are merely different are not yet two tones a reader can tell apart
 * down a column. The bug that forced this palette open was `red` beside an
 * orange `amber` (`#b45309`) at the old 14%: 24° apart, ΔE 5.4, one pale peach
 * with two names.
 *
 * Eight hues will not fit in a 14% or even an 18% tint — the best eight-hue set
 * at 18% collapses to ΔE 7.3, back inside the range that caused the complaint.
 * At 26% the same eight hold ΔE ≥ 10.6 across every pair, which is the floor
 * the six-tone version met, and each still carries its ink at 4.9:1 on its own
 * fill. So the trade is a more saturated cell for twice the palette, and it is
 * a deliberate one: `value-format.test.ts` asserts both numbers.
 *
 * `red`/`pink` is the binding pair and the one to watch if a ninth is ever
 * wanted. Orange is still ABSENT, because it is the hue that folds back into
 * `red`; `amber` is gold (`#ca8a04`), ΔE 19.4 from `red` — a yellower citron
 * was tried and drifts toward `green`.
 *
 * APPEND ONLY. A saved report stores the NAME, so removing or renaming a tone
 * un-parses every spec that used it. `amber` keeps its name through its retune
 * from orange to gold for that reason.
 */
export const RULE_TONES = [
  'red',
  'amber',
  'green',
  'sky',
  'blue',
  'purple',
  'pink',
  'grey',
] as const;
export type RuleTone = (typeof RULE_TONES)[number];

/**
 * One highlight rule: paint this cell when its value compares this way.
 *
 * 🔴 `value` IS IN THE UNITS THE READER SEES, not the units the measure
 * stores. A percent measure carries a RATIO (0.612 renders as `61.2%`), so a
 * rule written against what is on screen says `100`, not `1`. `toneFor` does
 * the conversion, keyed off the RESOLVED format, which means a measure put
 * under a `showAs` share re-reads its own thresholds as percentage points —
 * correct, because the number they are being compared against is now a share,
 * and surprising enough to be worth saying out loud. See `value-format.ts`.
 */
export const formatRuleSchema = z.object({
  op: z.enum(RULE_OPS),
  value: z.number().finite(),
  /**
   * 🔴 AN UNKNOWN TONE DEGRADES TO `grey`; IT DOES NOT FAIL THE PARSE.
   *
   * `z.enum(RULE_TONES)` here failed the whole `reportSpecSchema` — one
   * display attribute taking the name, the description, the filters and every
   * other edit down with it. That is the bug `valueFormatSchema` below already
   * documents itself as having fixed for unknown KEYS; unknown enum VALUES did
   * it too, and the fix never reached them.
   *
   * It matters now because `RULE_TONES` has started growing, and the services
   * deploy separately (`deploy-qa.yml` builds only what a paths filter
   * matched). So a browser on a newer bundle can save a tone an older API pod
   * has never heard of — and a rollback is worse than the skew window, because
   * specs already holding the newer tone have to stay readable by the code
   * that comes back.
   *
   * `grey` rather than dropping the rule: the threshold and its precedence are
   * what the author actually decided, and they survive. Only the colour is
   * lost, and `grey` is the tone that carries no verdict — degrading to `red`
   * would invent an alarm the author never raised. This matches how the rest
   * of the layer degrades (an unknown `MeasureFormat` falls back to `number`).
   *
   * STILL `z.string()`, so this stays a required field of the right type: a
   * rule that arrives with no tone at all, or a number, is a bug in the client
   * that built it and is refused as loudly as before. Only a string naming a
   * tone this build cannot paint is forgiven.
   */
  tone: z
    .string()
    .transform(
      (t): RuleTone => ((RULE_TONES as readonly string[]).includes(t) ? (t as RuleTone) : 'grey'),
    ),
});
export type FormatRule = z.infer<typeof formatRuleSchema>;

/**
 * One measure's display attributes, as the reader chose them.
 *
 * EVERY KEY OPTIONAL, AND ABSENCE MEANS INHERIT — never a materialised
 * default. A report that never opted out of the unit's precision still moves
 * when `FORMAT_DECIMALS` moves; one that stored `decimals: 2` at the moment it
 * was built would be frozen at a number nobody chose.
 *
 * UNKNOWN KEYS ARE STRIPPED, not refused. This was `.strict()`, which failed
 * the whole `reportSpecSchema` parse — so a browser holding a newer bundle
 * against an older API pod lost the name, the description and every other edit
 * in the spec, over one display attribute. Everything else in this layer
 * degrades instead: an unknown `MeasureFormat` falls back to `number`, an
 * out-of-range precision falls back to the unit's. The resolver already
 * ignores keys that do not apply, so a stripped one costs nothing.
 */
export const valueFormatSchema = z.object({
  decimals: z.number().int().min(0).max(MAX_DECIMALS).optional(),
  negative: z.enum(NEGATIVE_STYLES).optional(),
  zero: z.enum(ZERO_STYLES).optional(),
  symbol: z.enum(SYMBOL_STYLES).optional(),
  /** Thousands separators. Absent (and `true`) both mean grouped. */
  separators: z.boolean().optional(),
  /** Show in thousands (`K`) or millions (`M`). Absent reads the value whole. */
  scale: z.enum(SCALE_STYLES).optional(),
  /**
   * Currency with the symbol and decimal point aligned down the column.
   *
   * NOT a fifth `MeasureFormat`: it is currency, and it resolves to the
   * currency unit. It fixes the negative style to brackets and the zero to a
   * hyphen, so neither is offered alongside it.
   */
  accounting: z.boolean().optional(),
  /**
   * Highlight rules, FIRST MATCH WINS.
   *
   * Ordered rather than a set, because two rules can cover one value — `< 0`
   * red and `< -40` deep red both fire at -50 — and "whichever the author put
   * first" is the only precedence a reader can predict without being told the
   * sorting rule. It also makes a band expressible without a `between` op.
   *
   * ORTHOGONAL TO `negative`: a rule paints the cell's BACKGROUND, `negative`
   * colours the TEXT. Both may apply to the same cell and neither overrides the
   * other, so an author can keep the red minus they already read by and add a
   * fill on top.
   */
  rules: z.array(formatRuleSchema).max(MAX_FORMAT_RULES).optional(),
});
export type ValueFormat = z.infer<typeof valueFormatSchema>;

/**
 * How a pivot's result is drawn.
 *
 * `area` is a filled `line` and carries the same requirement — a time
 * dimension on the row axis — because the fill is only readable over an
 * ordered axis. `metric` is one measure over one period, drawn as
 * the number itself. It is a VIZ rather than a third {@link ReportKind}
 * because it asks the same question a pivot does and compiles identically;
 * only the drawing differs, which is exactly what this field decides.
 */
export const VIZ_KINDS = ['table', 'bar', 'line', 'area', 'pie', 'metric'] as const;
export type VizKind = (typeof VIZ_KINDS)[number];

/**
 * Ceiling on group-by depth ACROSS BOTH AXES (FR-2.5). Pivot only.
 *
 * A READABILITY bound, and a loose one. It is NOT a cost control, because depth
 * is a poor proxy for cost: the dimensions here are heavily correlated, so an
 * employee has one manager, one pay type and one tenure band, and grouping by
 * all four yields exactly as many groups as grouping by employee alone. Ten
 * employee attributes are free. ONE independent dimension is not — employee ×
 * customer is already past {@link DEFAULT_PIVOT_LIMIT}, at a depth of two.
 *
 * So the number that guards the expensive shape is the group cap, not this one,
 * and this one was turning away the cheap shape it happened to sit in front of:
 * four employee attributes, which is the report people actually asked for.
 *
 * What depth DOES cost is subtotal rows, which grow with it and are computed
 * synchronously under the builder's live preview. Measured on a 5000-group
 * result: ~50ms of layout at four, ~650ms at ten with subtotals on, ~55ms at
 * ten with them off. If that becomes the complaint, the bound to tighten is on
 * SUBTOTALS rather than on depth — turning one off is a control the reader
 * already has, and it is the half of the work that actually scales.
 */
export const MAX_GROUP_BY = 10;

/**
 * Rows on ONE PAGE of a listing when its spec names no page size.
 *
 * A listing's `limit` is a PAGE SIZE, not a ceiling on the answer. `offset`
 * walks the rest, `meta.totalRows` says how many there are altogether, and the
 * pager under the table is how a reader reaches row 50,001. It used to be a cap
 * with nothing past it: a listing showed 500 rows, said "capped — narrow the
 * period", and that was the end of the report — the rows it withheld were
 * frequently the ones somebody opened the report to find.
 *
 * Still bounded, and the bound is now on the PAGE rather than on the answer:
 * one response carrying a month of a tenant's timesheets is a browser tab that
 * stops responding, whatever the reader asked for.
 */
export const DEFAULT_DETAIL_LIMIT = 500;

/**
 * The page sizes a listing offers. `DEFAULT_DETAIL_LIMIT` must be one of them,
 * or the pager opens showing a size it cannot get back to.
 */
export const DETAIL_PAGE_SIZES = [100, 250, 500, 1000] as const;

/**
 * Most rows one LISTING response may carry.
 *
 * Its own number, because `limit` is one field serving two kinds and the pivot
 * ceiling is not an argument about listings. It used to be the schema's `.max`
 * that bounded this, so binding that `.max` to {@link DEFAULT_PIVOT_LIMIT}
 * silently handed a listing the pivot's ceiling — and `/api/analytics/query`
 * takes its spec from a request body, so that is a bound anyone holding
 * `report.view` can ask for, not merely something the pager might write.
 * `planDetail` clamps to it; the UI never approaches it
 * ({@link DETAIL_PAGE_SIZES} stops at 1,000).
 */
export const MAX_DETAIL_LIMIT = 25_000;

/**
 * Groups a pivot returns when its spec names no cap.
 *
 * A pivot was assumed to need no default, on the reasoning that the group count
 * IS the row count and a handful of group-bys is a page. That does not hold for
 * the dimensions this model actually offers: employee × customer × activity ×
 * month over a year is tens of thousands of existing combinations, and an
 * uncapped statement streams every one of them into a browser with
 * `truncated: false` beside it. A ceiling rather than a page size — it is the
 * number at which a pivot has stopped being readable, not one anybody chose to
 * see.
 *
 * WHY 25,000 AND NOT 5,000. The first number was a guess, and a month of one
 * tenant's timesheets walked straight through it: Customer × Employee ×
 * Billable over August 2026 is 5,866 groups, so the report that shape was
 * written for came back truncated — 31% of the month missing, under a grand
 * total that still printed as though it were the answer.
 *
 * The binding constraint was never SQL or memory. 5,866 rows of
 * `{dims, values}` is ~2.4 MB, and 25,000 is ~10 MB on both hosts that project
 * one (this runs in the browser AND on the API for the export). It is LAYOUT,
 * and layout is not linear in groups: `rowsUnder()` in `result.ts` answers each
 * subtotal or folded band by scanning the whole row axis, memoised per band, so
 * the work is bands × rows and both grow together. A pivot folded at customer
 * is one band per customer — 1,055 × 4,687 ≈ 4.9M comparisons at the old
 * ceiling, and the same shape at 25,000 is nearer 140M. Five times the ceiling
 * is roughly TWENTY-FIVE times that work, synchronously, in the browser and
 * again in the API's buffered export.
 *
 * `collapsed` does not answer this. It hides rows from the DOM — which is what
 * made the Clientwise report usable again — but a folded caption is still a
 * band, so the scan above runs either way. The controls that do reduce it are
 * turning SUBTOTALS off and narrowing the period.
 *
 * So the number is a deliberate trade and not a free one: it was raised because
 * a correct report mattered more than a slow one, and the shape to watch is
 * deep AND subtotalled at the new ceiling. If that becomes the complaint, the
 * fix is to index the row axis by prefix once rather than to lower this back.
 *
 * Raising it does not make a truncated pivot honest; it makes truncation rare.
 * The report that runs into the new ceiling is still reporting over part of its
 * answer, which is why `ResultMeta.truncated` is now said out loud above the
 * grid and on the total row, rather than noted in a footer.
 */
export const DEFAULT_PIVOT_LIMIT = 25_000;

/**
 * The window a period may fall in, and the longest one it may be.
 *
 * A period is not just a filter: a model's capacity SELECT builds its rows from
 * `generate_series(from, to, <grain>)` and crosses them with every visible
 * employee, so the span is a MULTIPLIER on the work the database does — one day
 * per row at the finest grain. A typo that puts a year at 0002 asks for seven
 * hundred thousand periods per employee, and nothing downstream would refuse it.
 *
 * Bounded here rather than in the compiler because it is a property of the
 * REQUEST, and the same object is the request body, a saved report and a
 * widget's settings — all three need the same answer. Generous enough that no
 * real report meets it: five years is longer than this business has existed.
 */
export const MIN_PERIOD_DATE = '2000-01-01';
export const MAX_PERIOD_DATE = '2100-01-01';
export const MAX_PERIOD_DAYS = 366 * 5;

/** Days between two `YYYY-MM-DD` dates, or `null` if either is not a date. */
export function periodDays(from: string, to: string): number | null {
  const a = Date.parse(`${from}T00:00:00Z`);
  const b = Date.parse(`${to}T00:00:00Z`);
  if (Number.isNaN(a) || Number.isNaN(b)) return null;
  return Math.round((b - a) / 86_400_000) + 1;
}

/**
 * Is this period one a report may be asked for? Shared so the builder can
 * refuse it before it is typed and the server can refuse it on arrival.
 */
export function periodProblem(from: string, to: string): string | null {
  if (from < MIN_PERIOD_DATE || to < MIN_PERIOD_DATE) {
    return `Dates before ${MIN_PERIOD_DATE} are not reportable.`;
  }
  if (from > MAX_PERIOD_DATE || to > MAX_PERIOD_DATE) {
    return `Dates after ${MAX_PERIOD_DATE} are not reportable.`;
  }
  // BACKWARDS, which every caller has to refuse and only one of them did. The
  // builder's period control had its own check; a spec and a members request
  // had none, so `BETWEEN` over an inverted window matched nothing and the
  // answer came back empty — a statement about the data for what is actually a
  // malformed request. Stated here so all three read one rule.
  if (to < from) return 'The end date is before the start date.';
  const days = periodDays(from, to);
  if (days === null) return 'The period is not a pair of dates.';
  if (days > MAX_PERIOD_DAYS) {
    return `A report covers at most ${MAX_PERIOD_DAYS} days; this one asks for ${days}.`;
  }
  return null;
}

/**
 * Whether a sort orders a level by the level itself — its name (`'label'`) or
 * its key (`'key'`) — rather than by a measure. The one test every layer that
 * reads a sort entry needs, so a new literal is added in one place.
 */
export const isLevelSort = (by: string): boolean => by === 'label' || by === 'key';

const sortSchema = z.object({
  /**
   * A dimension id, a measure id, or one of two literals: `'label'`, the
   * level's name, or `'key'`, the level's KEY — the order the query sends,
   * which the builder calls "Data source order".
   *
   * `'key'` exists for its DIRECTION: it is how a pivot says "newest month
   * first". Every time dimension keys on text that sorts chronologically, so
   * descending-by-key is reverse calendar order, where descending-by-label is
   * reverse alphabetical. Both directions are compared by one collator (see
   * `orderTuples`), so descending is always exactly ascending reversed; a
   * level with NO entry keeps the order the server sent instead.
   */
  by: z.string(),
  dir: z.enum(['asc', 'desc']),
  /**
   * Column tuple KEYS to rank by, when sorting rows by a measure.
   *
   * Clicking March's header should order by March, not by the measure summed
   * across every month — four headers producing one identical order reads as
   * broken. Absent means rank by the total across the axis.
   */
  at: z.array(z.string()).optional(),
  /**
   * PIVOT: the row or column dimension whose LEVEL this entry orders.
   *
   * A pivot's axis is nested, and an order is something each level has of its
   * own — "customers by their total hours, employees inside each customer
   * alphabetically" is two rules, not a chain of two rules applied to both
   * levels. So the entry names the level it governs, and every level takes at
   * most one entry.
   *
   * Absent is the ORIGINAL shape and still means what it meant: a comparison
   * chain over the whole axis, first entry deciding and the rest breaking its
   * ties, applied identically at every level. Kept, rather than migrated away,
   * because it is what every report saved before this parses as — and NOT
   * rewritten into per-level entries either, because the two are not the same
   * order: a whole-axis chain over an unpivoted axis is an `ORDER BY` on the
   * flat result, ranked by the LEAF value, where per-level entries rank each
   * level by its GROUP TOTAL in memory. See {@link orderTuples} for how the
   * two live together on one axis.
   *
   * A listing has no levels, so its `rowSort` never carries this.
   */
  on: z.string().optional(),
});
export type SortSpec = z.infer<typeof sortSchema>;

/**
 * The macros a report may be asked over — the standard list, and no more.
 *
 * `all_time` is excluded because it resolves to NO window, and a report's
 * period is not optional: the model's capacity SELECT builds a row per period
 * per employee, so "no bounds" is a statement nothing downstream can compile.
 * `current_week` is excluded for the plainer reason that no report control
 * offers it. Both are still legal {@link DateRangePreset}s elsewhere.
 */
export type ReportPeriodPreset = (typeof DATE_RANGE_PRESETS)[number];

/**
 * The macro a spec can store, for a selection made by a control that may
 * offer more than a report can. Anything a report cannot express is `custom`
 * — the dates still stand, only the name for them is dropped.
 */
export function reportPeriodPreset(preset: DateRangePreset): ReportPeriodPreset {
  return (DATE_RANGE_PRESETS as readonly DateRangePreset[]).includes(preset)
    ? (preset as ReportPeriodPreset)
    : 'custom';
}

/** Everything both spec shapes carry. */
const baseShape = {
  model: z.string().min(1),
  kind: z.enum(REPORT_KINDS).default('pivot'),
  /**
   * HOW the period was chosen: a macro like `previous_quarter`, or `custom`
   * for two dates somebody typed. Absent on a spec written before this field
   * existed, which reads the same as `custom`.
   *
   * The period is a filter like any other, and a filter records what was
   * ASKED for. "Current month" is a question that re-answers itself — a report
   * saved under that macro is a report about whatever month it is opened in,
   * which is the entire reason somebody picks a macro instead of typing two
   * dates. Storing only the dates it happened to resolve to on the day it was
   * saved turns that standing question into a fixed one, silently, some time
   * after the person who asked it has stopped looking.
   *
   * So this is the load-bearing field and `from`/`to` are its ANSWER — see
   * {@link withResolvedPeriod}, which recomputes them.
   */
  period: z.enum(DATE_RANGE_PRESETS).optional(),

  /**
   * Inclusive ISO dates — the window itself. The host maps these onto the
   * model's period column.
   *
   * Always present and always concrete, even under a macro: everything
   * downstream of the builder — the compiler, `periodProblem`, the SQL, the
   * export's About sheet — reads the WINDOW and never the macro, so there is
   * exactly one answer to "which dates did this report run over" and it is the
   * one in the statement. Under a macro these are a snapshot that
   * {@link withResolvedPeriod} refreshes whenever the spec is loaded.
   */
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'from must be YYYY-MM-DD'),
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'to must be YYYY-MM-DD'),

  /**
   * The conditions, ANDed.
   *
   * A node is a RULE — column, operator, value — or a GROUP of rules under one
   * connector. One level deep and no deeper: a group of groups has no honest
   * rendering on a filter bar, and a filter nobody can read on the bar is the
   * thing this design exists to avoid. See docs/reporting/filter-operators.md.
   *
   * NO LEGACY SHAPE. Specs written before operators existed held
   * `{ customer: ['a','b'] }`, and they are MIGRATED — see the Migration
   * section of the PR that introduced this field. A reader that also accepted
   * the old record would be a second definition of what a filter is, living in
   * the codebase for as long as anybody left it there; refusing it means an
   * unmigrated row fails loudly, at the point where the fix is one statement.
   */
  filters: z.array(filterNodeSchema).max(20).default([]),

  /**
   * How an axis is ordered, as a comparison CHAIN: the first entry decides,
   * later entries break its ties. A list rather than one entry because "by
   * region, then by revenue within it" is an ordinary thing to ask for and
   * cannot be said with a single key.
   */
  rowSort: z.array(sortSchema).default([]),
  colSort: z.array(sortSchema).default([]),
  /** `{ measureId: mode }`. Absent means `'raw'`. */
  showAs: z.record(z.string(), z.enum(SHOW_AS_MODES)).default({}),
  /**
   * `{ measureId: ValueFormat }` — how each measure READS, as the report author
   * chose it. Absent means the measure's own unit decides.
   *
   * Display only: it never changes a value, never reaches `planKey`, and both
   * exports mirror the display. Shaped like `showAs` — a record keyed by
   * measure id, defaulted to `{}` — so every spec ever saved parses unchanged,
   * and an entry naming a measure that is no longer on the shelf matches
   * nothing and costs nothing.
   *
   * The cap is a bound on a request body, not on a reader, as it is for
   * `toggledGroups` — a report has tens of measures, and an entry per measure
   * is the only shape this legitimately takes.
   */
  valueFormat: z
    .record(z.string(), valueFormatSchema)
    .refine((v) => Object.keys(v).length <= MAX_VALUE_FORMATS, {
      message: `at most ${MAX_VALUE_FORMATS} measures may carry a format`,
    })
    .default({}),
  totals: z
    .object({ row: z.boolean().default(false), col: z.boolean().default(false) })
    .default({ row: false, col: false }),

  /**
   * Row dimensions whose repeated values are blanked, by id.
   *
   * Per dimension rather than one switch, because the outer level is the one
   * that repeats worth hiding — the inner level repeating is usually the
   * information. Display only: it never changes a value, and exports mirror
   * the display.
   */
  suppressRepeats: z.array(z.string()).default([]),

  /**
   * Row dimensions that get a subtotal band beneath each of their groups, by
   * id. The innermost level is excluded even if named — a subtotal of one row
   * is that row printed twice.
   */
  subtotals: z.array(z.string()).default([]),

  /**
   * Row dimensions whose groups OPEN folded, by id — each group at that level
   * shows as one row carrying its totals, until the reader unfolds it. The
   * innermost level is ignored even if named: a group of one row folded is
   * that row.
   *
   * What lets one saved report be both the summary and the detail: saved
   * folded at Customer it opens as a customer list, and the reader unfolds the
   * customers they came for. Display only, like the two flags above — the
   * query is the same either way, and `planKey` never sees it.
   *
   * Optional rather than defaulted, as `period` is, so the specs written
   * before it existed — and every one built in code — still type-check.
   */
  collapsed: z.array(z.string()).optional(),

  /**
   * Groups the reader flipped away from their level's default, by
   * `groupKey()` — `"0:<customer id>"`. A flip rather than a state, so
   * folding a whole level is one change to `collapsed` and not one entry per
   * group.
   *
   * Stored in the spec for the reason a header's sort is: the export body IS
   * the spec, and a file that ignored what the reader had folded would not be
   * the screen it came from. A key for a group that is not in this result
   * matches nothing and costs nothing, which is what lets it survive a filter
   * change. The cap is a bound on a request body, not on a reader.
   */
  toggledGroups: z.array(z.string().max(2000)).max(10_000).optional(),

  viz: z.enum(VIZ_KINDS).default('table'),
  /**
   * Whether a `bar` or `area` stacks its series rather than grouping them.
   *
   * A presentation choice, not a question change — the same numbers either
   * way — so it rides beside `viz` instead of forking the vocabulary into
   * `bar` and `stackedBar`. Meaningless without a series axis, and meaningless
   * for a non-additive measure, both of which {@link canVisualize} refuses:
   * stacking a ratio draws a total that does not exist.
   */
  stacked: z.boolean().default(false),
  /**
   * What the reader asked to see: a listing's PAGE SIZE, a pivot's CAP.
   *
   * Bounded by {@link DEFAULT_PIVOT_LIMIT} rather than by a literal of its own,
   * and that is the point of naming it here. The two numbers have to agree: a
   * pivot whose rows are ordered after the query carries this value as the
   * MEMORY cap (`plan.memory.limit`) while the ceiling caps SQL, so a bound
   * below the ceiling would fetch every group and then drop the tail on the
   * floor — a second truncation, in the projection, with nothing in `meta`
   * saying it happened. They were both 5,000 by coincidence; now one is
   * derived.
   *
   * It bounds a LISTING's page size too, and a schema `.max` is the wrong place
   * to decide that: the endpoint takes this field from a request body, so the
   * bound is what any caller may ask for rather than what the pager writes.
   * {@link MAX_DETAIL_LIMIT} is the listing's own number and `planDetail`
   * clamps to it, so the two kinds can diverge without this line moving.
   */
  limit: z.number().int().min(1).max(DEFAULT_PIVOT_LIMIT).optional(),

  /**
   * DETAIL ONLY. The fields listed, left to right — dimension ids, and measure
   * ids for measures that declare a row-level expression.
   *
   * On the base shape rather than a second schema so a draft can hold one
   * before it is runnable, the way `rows` and `measures` do.
   */
  columns: z.array(z.string()).default([]),
  /**
   * DETAIL ONLY. Rows to skip — the page, taken in the order the query ran.
   *
   * With `limit` as the page size, this is the field that makes the whole
   * answer reachable rather than merely the first page of it.
   *
   * Meaningless for a pivot, where the group count is the row count and the
   * axis is often ordered after the projection: a page taken in a different
   * order than the one displayed shows the wrong members.
   */
  offset: z.number().int().min(0).optional(),

  /**
   * The FIGURE this report is the basis of — set by a drill, never by the
   * builder. `{ model: 'utilization', measure: 'billableHours' }` on a
   * timesheets listing reads "these are the entries behind a Client Billable
   * Hours figure on utilization".
   *
   * It does two things, and the server does both (see `drillBasis`):
   *
   *  - LENDS the landing the measure the reader clicked when its own model does
   *    not offer it. Timesheets states Hours and Amount only; the entries behind
   *    a billable-hours cell have to be able to show billable hours, or the
   *    number the reader clicked appears nowhere on the page they land on. Only
   *    between models that read the same facts, which is what makes the lent
   *    expression mean the same thing on both.
   *  - On a LISTING, keeps only the rows where that measure is non-zero. Client
   *    Billable Hours is a column that is 0 on every non-billable entry, so
   *    without this "the 212 entries behind 40 hours" is 40 hours and 170 rows
   *    of zeros.
   *
   * A ratio lists its NUMERATOR — the part of it an entry carries. A figure no
   * entry carries at all (available hours are working days × 8) sets no basis:
   * the landing lists every row the coordinate selects, which is what "the
   * rows behind this number" can honestly mean for it.
   */
  basis: z.object({ model: z.string().min(1), measure: z.string().min(1) }).optional(),

  /**
   * The model of the report a drill chain STARTED on — set by a drill, never by
   * the builder. `'utilization'` on a timesheets listing reads "these rows are
   * being read on behalf of a Utilization report".
   *
   * It decides WHOSE ROWS the landing may show. Two models over the same facts
   * can still differ in which rows a caller is shown — Utilization reaches an
   * entry by the caller's HR line alone, Timesheets by the line or by an
   * account the caller leads — so a landing read under its own model's rule
   * can list rows the figure never counted. The server runs the landing under
   * the ORIGIN's rule instead, which is what makes the rows add back up to the
   * cell. Only between models that read the same facts; see `resolveSpec`.
   *
   * THE FIRST REPORT'S, ALL THE WAY DOWN. A landing can be drilled again, and
   * the level below carries this value on unchanged rather than naming the
   * landing above it: the whole chain is one question asked of one report.
   *
   * Unlike {@link basis} it is set on EVERY drill, including one from a figure
   * no entry carries, because whose rows a reader may see does not depend on
   * which figure they clicked.
   */
  origin: z.string().min(1).optional(),
};

/**
 * A spec under construction. Shelves may be empty, so it is not runnable and
 * is never sent — the builder holds one of these and narrows it on submit.
 */
export const uiReportSpecSchema = z.object({
  ...baseShape,
  measures: z.array(z.string()).default([]),
  rows: z.array(z.string()).default([]),
  cols: z.array(z.string()).default([]),
});
export type UIReportSpec = z.infer<typeof uiReportSpecSchema>;

/**
 * Complete and runnable. The request body, and the saved form.
 *
 * "Complete" is per KIND, which is why the requirement is a refinement rather
 * than a `min(1)` on each field: a pivot needs a measure and a row dimension, a
 * listing needs a column and has neither. Refining rather than splitting into
 * two schemas keeps ONE request body, one saved shape, and one thing to parse.
 */
export const reportSpecSchema = z
  .object({
    ...baseShape,
    measures: z.array(z.string()).default([]),
    /** Group-by nesting down the side. At least one is required to aggregate. */
    rows: z.array(z.string()).default([]),
    /** Group-by nesting across the top. Empty means a flat list. */
    cols: z.array(z.string()).default([]),
  })
  .superRefine((spec, ctx) => {
    // Before anything about the shape, because the period is what multiplies
    // the work: a capacity SELECT builds a row per period per employee, so an
    // unbounded span is a query nobody can wait for whatever else the spec says.
    const period = periodProblem(spec.from, spec.to);
    if (period) ctx.addIssue({ code: 'custom', path: ['to'], message: period });

    if (spec.kind === 'detail') {
      if (spec.columns.length === 0) {
        ctx.addIssue({ code: 'custom', path: ['columns'], message: 'pick at least one column' });
      }
      return;
    }
    if (spec.measures.length === 0) {
      ctx.addIssue({ code: 'custom', path: ['measures'], message: 'pick at least one measure' });
    }
    // A KPI IS THE EXCEPTION, and the only one: it is a single measure over
    // the whole period with nothing to group by, so requiring a row dimension
    // would make the shape it exists to express unrepresentable. Every other
    // viz still needs one — a bar with no axis has nothing to draw bars for.
    if (spec.rows.length === 0 && spec.viz !== 'metric') {
      ctx.addIssue({ code: 'custom', path: ['rows'], message: 'pick at least one row dimension' });
    }
  });
export type ReportSpec = z.infer<typeof reportSpecSchema>;

/** Every group-by, in nesting order. Rows and columns are ONE `GROUP BY`. */
export function allGroupBy(spec: Pick<UIReportSpec, 'rows' | 'cols'>): string[] {
  return [...spec.rows, ...spec.cols];
}

export function showAsFor(spec: Pick<UIReportSpec, 'showAs'>, measureId: string): ShowAsMode {
  return spec.showAs?.[measureId] ?? 'raw';
}

/** What the author chose for one measure. `{}` where they chose nothing. */
export function valueFormatFor(
  spec: Pick<UIReportSpec, 'valueFormat'>,
  measureId: string,
): ValueFormat {
  return spec.valueFormat?.[measureId] ?? {};
}

/**
 * A spec with nothing chosen yet, for a fresh builder session.
 *
 * Takes a SELECTION rather than two dates, so a fresh builder can open on a
 * macro — the window it starts on is then the one the macro means today
 * rather than one computed once at mount.
 */
export function emptySpec(
  model: string,
  range: DateRangeValue,
  kind: ReportKind = 'pivot',
): UIReportSpec {
  const resolved = resolveDateRange(range);
  return {
    model,
    kind,
    period:
      range.preset === 'all_time' || range.preset === 'current_week' ? 'custom' : range.preset,
    from: resolved?.from ?? range.from ?? '',
    to: resolved?.to ?? range.to ?? '',
    measures: [],
    rows: [],
    cols: [],
    columns: [],
    filters: [],
    rowSort: [],
    colSort: [],
    showAs: {},
    valueFormat: {},
    totals: { row: false, col: false },
    suppressRepeats: [],
    subtotals: [],
    viz: 'table',
    stacked: false,
  };
}

/** True once a draft has enough to produce a result — per kind. */
export function isRunnable(spec: UIReportSpec): spec is ReportSpec {
  return spec.kind === 'detail'
    ? spec.columns.length > 0
    : // The same KPI exception the schema makes, and it has to be the same or
      // the builder offers Run on a spec the server then refuses.
      spec.measures.length > 0 && (spec.rows.length > 0 || spec.viz === 'metric');
}

/** The period as a date-range SELECTION, for the control that edits it. */
export function specDateRange(spec: Pick<UIReportSpec, 'period' | 'from' | 'to'>): DateRangeValue {
  return { preset: spec.period ?? 'custom', from: spec.from, to: spec.to };
}

/**
 * Recompute `from`/`to` from the spec's macro. The one place a stored macro
 * becomes dates.
 *
 * Call it wherever a spec ARRIVES — a saved report opening, a draft restored,
 * a drill — rather than where one runs. Resolving on arrival means the dates
 * on screen are the dates that will be queried, exported and saved back; a
 * spec that resolved itself later, on the way to the server, would run a
 * window nobody was shown.
 *
 * `today` is the caller's LOCAL calendar day, because "this month" means the
 * month on the calendar of the person asking. A spec whose period is `custom`
 * (or absent) is returned untouched, as is one whose macro already resolves to
 * the window it carries — same object, so a memo downstream does not fire.
 */
export function withResolvedPeriod<T extends Pick<UIReportSpec, 'period' | 'from' | 'to'>>(
  spec: T,
  today: Date = new Date(),
): T {
  if (!spec.period || spec.period === 'custom') return spec;
  const resolved = resolveDateRange({ preset: spec.period }, today);
  if (!resolved) return spec;
  if (resolved.from === spec.from && resolved.to === spec.to) return spec;
  return { ...spec, from: resolved.from, to: resolved.to };
}

export type { DateRangePreset, DateRangeValue };
// Re-exported so a consumer of a spec has one import for the period: the
// field, the macros it may hold, and the arithmetic that turns one into dates.
export { resolveDateRange };
